<template>
  <div class="crossbeam">
    <div v-if="initializationFailed" data-testid="application-failed">
      The application failed to load. Please try again or contact
      <a href="mailto:support@crossbeam.com">support@crossbeam.com</a>.
    </div>
    <router-view v-else v-slot="{ Component }">
      <transition name="fade">
        <div v-if="Component" id="full-app">
          <TalkToSalesModal
            v-if="talkToSalesModalVisible"
            @action="talkToSalesModalVisible = false"
            @close-modal="talkToSalesModalVisible = false"
          />
          <BillingPlanComparisonModal
            v-if="comparisonPlanModalVisible"
            :billing-interaction="billingInteraction as BillingInteraction"
            :billing-query
            @close-modal="comparisonPlanModalVisible = false"
          />
          <PaymentReminderDialog
            v-if="paymentDialogVisible"
            :visible="paymentDialogVisible"
            @close="onPaymentReminderClose"
          />
          <SearchModal
            v-if="isSearchModalVisible"
            :visible="isSearchModalVisible"
            @close="isSearchModalVisible = false"
          />
          <AIChatDrawer
            v-if="hasAccessToAiChat && aiChatMode === AI_CHAT_MODES.DRAWER"
            :visible="aiChatVisible"
            @ai-chat-closed="() => (aiChatVisible = false)"
          />
          <SsoLoginPrompt
            v-if="googleSsoPromptVisible && googleSsoEmailMatch"
            v-model:visible="googleSsoPromptVisible"
            :organization-name="googleSsoEmailMatch.organizationName"
            secondary-button-text="Continue with Google"
            @continue-sso="handleGoogleSsoContinue"
            @dismiss="handleGoogleSsoDismiss"
          />
          <component
            :is="Component"
            v-if="
              !isCheckingGoogleSso &&
              !googleSsoPromptVisible &&
              !isRedirectingGoogleSso
            "
            id="content"
          />
        </div>
      </transition>
    </router-view>
  </div>
</template>

<script setup lang="ts">
import { Nullable } from '@crossbeam/types';

import { useHead } from '@unhead/vue';
import { whenever } from '@vueuse/core';
import { computed, nextTick, onBeforeUnmount, provide, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import PaymentReminderDialog from '@/components/billing/PaymentReminderDialog.vue';
import TalkToSalesModal from '@/components/billing/TalkToSalesModal.vue';
import SsoLoginPrompt from '@/components/login/SsoLoginPrompt.vue';
import AIChatDrawer from '@/components/navbar/AIChatDrawer.vue';
import SearchModal from '@/components/navbar/SearchModal.vue';

import { useAIChatAccess } from '@/composables/mcp/useAIChatAccess';
import { useAIChatState } from '@/composables/mcp/useAIChatState';
import { useAppcues } from '@/composables/useAppcues';
import useAuth from '@/composables/useAuth';
import useGoogleSsoPostLoginPrompt from '@/composables/useGoogleSsoPostLoginPrompt';
import useMinimalApp from '@/composables/useMinimalApp';
import { usePaymentDialog } from '@/composables/usePaymentDialog';
import { ORGANIZATION_PAYMENT_REQUIRED } from '@/constants/feature_flags';
import { AI_CHAT_MODES } from '@/constants/mcp';
import { captureException } from '@/errors';
import {
  ProvidedOpenComparisonPlanModal,
  ProvidedOpenTalkToSalesModal,
} from '@/injectionKeys/billing';
import {
  ProvidedAIChatMode,
  ProvidedAIChatState,
  ProvidedAIChatVisible,
  ProvidedSearchModalVisible,
} from '@/injectionKeys/global';
import { useFeatureFlagStore } from '@/stores';
import { BillingInteraction, BillingQuery } from '@/types/billing';

import BillingPlanComparisonModal from './billing/BillingPlanComparisonModal.vue';

const route = useRoute();
const router = useRouter();
const { initializationFailed, inMaintenanceMode } = useAuth();
const ffStore = useFeatureFlagStore();

const openIntercom = computed(() => route.query.intercom === 'open');
const openAiChat = computed(() => route.query.ai_chat === 'open');

const { hasAccessToAiChat } = useAIChatAccess();

const {
  isPromptVisible: googleSsoPromptVisible,
  ssoEmailMatch: googleSsoEmailMatch,
  isChecking: isCheckingGoogleSso,
  isRedirecting: isRedirectingGoogleSso,
  handleContinueWithSso: handleGoogleSsoContinue,
  handleContinueWithPassword: handleGoogleSsoDismiss,
} = useGoogleSsoPostLoginPrompt();

const isSearchModalVisible = ref(false);
provide(ProvidedSearchModalVisible, isSearchModalVisible);

const aiChatVisible = ref(false);
const aiChatMode = ref(AI_CHAT_MODES.DRAWER);
provide(ProvidedAIChatVisible, aiChatVisible);
provide(ProvidedAIChatMode, aiChatMode);

const talkToSalesModalVisible = ref(false);
const billingInteraction = ref<Nullable<BillingInteraction>>(null);
const billingQuery = ref<Nullable<BillingQuery>>(null);
provide(ProvidedOpenTalkToSalesModal, () => {
  talkToSalesModalVisible.value = true;
});

const comparisonPlanModalVisible = ref(false);
provide(
  ProvidedOpenComparisonPlanModal,
  (opts: BillingInteraction, route: BillingQuery) => {
    billingInteraction.value = opts;
    billingQuery.value = route;
    comparisonPlanModalVisible.value = true;
  },
);
/**
 * We are watching for maintenance mode state coming from statuspage. If we have a
 * valid event, we punt user to the maintenance mode page.
 *
 * We punt the user back home if the old maintenance event is in state and has been reset to null
 * because no in_progress events are coming through. This essentially means maintenance mode has
 * been marked as completed
 */
watch(inMaintenanceMode, (newVal, oldVal) => {
  if (newVal) router.push({ name: 'maintenance_mode' });
  else if (!!oldVal && !newVal) router.push({ name: 'home' });
});

useHead({
  title: 'Crossbeam',
});

const { isMinimalView } = useMinimalApp();

if (hasAccessToAiChat.value && !isMinimalView) {
  provide(ProvidedAIChatState, useAIChatState());
}

const organizationPaymentRequired = computed(() =>
  ffStore.hasFeatureFlag(ORGANIZATION_PAYMENT_REQUIRED),
);

// Payment reminder dialog logic
const paymentReminderTimer = ref<ReturnType<typeof setTimeout> | null>(null);

// Payment dialog composable for external triggers
const { paymentDialogVisible, showPaymentDialog, hidePaymentDialog } =
  usePaymentDialog();

const PAYMENT_REMINDER_INTERVAL_MS = 30 * 1000;

function startPaymentReminderTimer() {
  if (paymentReminderTimer.value) {
    clearTimeout(paymentReminderTimer.value);
  }
  paymentReminderTimer.value = setTimeout(() => {
    if (organizationPaymentRequired.value) {
      showPaymentDialog();
      startPaymentReminderTimer(); // Restart the timer for next reminder
    }
  }, PAYMENT_REMINDER_INTERVAL_MS);
}

function onPaymentReminderClose() {
  hidePaymentDialog(); // Hide external payment dialog too
  startPaymentReminderTimer();
}

// Watch for payment required flag changes
watch(
  organizationPaymentRequired,
  (isRequired) => {
    if (isRequired) {
      // Show initial dialog immediately
      showPaymentDialog();
      startPaymentReminderTimer();
    } else {
      // Clear timer if payment is resolved
      if (paymentReminderTimer.value) {
        clearTimeout(paymentReminderTimer.value);
        paymentReminderTimer.value = null;
      }
      hidePaymentDialog();
    }
  },
  { immediate: true },
);

// Clean up timer on unmount
onBeforeUnmount(() => {
  if (paymentReminderTimer.value) {
    clearTimeout(paymentReminderTimer.value);
  }
});

const triggerRouteCheck = computed(() => {
  return route.name && !isMinimalView;
});
whenever(
  triggerRouteCheck,
  async () => {
    if (openIntercom.value && window.Intercom) {
      await nextTick();
      try {
        window.Intercom('show');
      } catch (err) {
        captureException(err);
      }
    }
    if (openAiChat.value && hasAccessToAiChat.value) {
      aiChatVisible.value = true;
    }
  },
  { immediate: true, once: true },
);

useAppcues();

defineExpose({
  showPaymentDialog,
  ffStore,
  openAiChat,
  aiChatVisible,
  openIntercom,
  onPaymentReminderClose,
  paymentReminderTimer,
  organizationPaymentRequired,
  initializationFailed,
});
</script>

<style lang="pcss">
#app {
  display: flex;
  flex-direction: column;
}
.crossbeam {
  position: relative;
  min-height: 100vh;
  height: 100vh;
}
@media (max-width: 768px) {
  .intercom-lightweight-app {
    display: none;
  }
}
</style>
